import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { selectAlerts, removeAlert } from '../../store/slices/uiSlice.js';

const AlertSystem = () => {
  const dispatch = useDispatch();
  const alerts = useSelector(selectAlerts);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-400" />;
      case 'error':
        return <XCircleIcon className="h-6 w-6 text-red-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-6 w-6 text-blue-400" />;
    }
  };

  const getAlertStyles = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const handleCloseAlert = (alertId) => {
    dispatch(removeAlert(alertId));
  };

  useEffect(() => {
    // Auto-close alerts based on their duration
    alerts.forEach((alert) => {
      if (alert.autoClose && alert.duration) {
        const timer = setTimeout(() => {
          dispatch(removeAlert(alert.id));
        }, alert.duration);

        return () => clearTimeout(timer);
      }
    });
  }, [alerts, dispatch]);

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-4 max-w-sm w-full">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out animate-slide-in ${getAlertStyles(alert.type)}`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {getAlertIcon(alert.type)}
            </div>
            <div className="ml-3 flex-1">
              {alert.title && (
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  {alert.title}
                </h3>
              )}
              <p className="text-sm text-gray-700">
                {alert.message}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
                onClick={() => handleCloseAlert(alert.id)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlertSystem;